import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import complianceData from '../content/compliance/index.json';
import YourRightsSection from '../components/sections/YourRightsSection';
import * as Icons from 'lucide-react';
import { 
  Scale, 
  Building2, 
  Users, 
  Cpu, 
  CheckCircle2, 
  ExternalLink,
  Network,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  Scale: Scale,
  Building2: Building2,
  Users: Users,
  Cpu: Cpu,
  Network: Network,
  Shield: Icons.Shield
};

const CompliancePage = () => {
  const navigate = useNavigate();
  
  const getCategoryIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Scale;
    return <IconComponent className="w-6 h-6" />;
  };
  
  const handleJoinWaitingList = () => {
    navigate('/');
    // Use setTimeout to allow the page to fully load before scrolling
    setTimeout(() => {
      const waitingListSection = document.getElementById('waiting-list');
      if (waitingListSection) {
        // Scroll with offset to account for fixed header
        const yOffset = -100;
        const y = waitingListSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#1F1E1E] to-[#2D2A2A] text-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/20 px-4 py-2 rounded-full mb-6">
              <Scale className="w-5 h-5 text-[#D4722F]" />
              <span className="text-sm font-semibold text-[#D4722F] uppercase tracking-wide">
                Compliance & Standards
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold max-w-4xl leading-tight mb-6">
              {complianceData.heading}
            </h1>
            
            <p className="text-xl text-gray-400 max-w-3xl">
              {complianceData.subheading}
            </p>

            {complianceData.disclaimer && (
              <div className="mt-8 max-w-3xl bg-white/10 border border-white/15 rounded-xl p-5" data-testid="compliance-disclaimer">
                <p className="text-sm font-bold text-[#D4722F] uppercase tracking-wide mb-1.5">
                  {complianceData.disclaimer.heading}
                </p>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {complianceData.disclaimer.body}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="sticky top-20 z-40 bg-white border-b border-gray-200 py-4 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {complianceData.categories.map((category, index) => (
                <a
                  key={index}
                  href={`#${category.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B5541B]/10 text-[#B5541B] hover:bg-[#B5541B] hover:text-white transition-all text-sm font-medium"
                >
                  {getCategoryIcon(category.icon)}
                  {category.title}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {complianceData.categories.map((category, categoryIndex) => (
            <section 
              key={categoryIndex}
              id={category.title.toLowerCase().replace(/\s+/g, '-')}
              className="mb-20 scroll-mt-32"
            >
              {/* Category Header */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-14 h-14 rounded-2xl bg-[#B5541B] flex items-center justify-center text-white">
                  {getCategoryIcon(category.icon)}
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-[#1F1E1E]">
                  {category.title}
                </h2>
              </div>
              {category.intro && (
                <p className="text-[#3B3535]/70 leading-relaxed -mt-6 mb-8 max-w-3xl">{category.intro}</p>
              )}

              {/* Items Grid */}
              <div className="space-y-8">
                {category.items.map((item, itemIndex) => (
                  <div 
                    key={itemIndex}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
                  >
                    {/* Item Header */}
                    <div className="p-6 lg:p-8 border-b border-gray-100">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                          <h3 className="text-xl lg:text-2xl font-bold text-[#1F1E1E]">
                            {item.name}
                          </h3>
                          {item.applies && (
                            <span className={`inline-block mt-2 text-xs font-semibold px-3 py-1 rounded-full ${
                              item.guidance
                                ? 'bg-gray-100 text-[#3B3535]/70 border border-gray-200'
                                : 'bg-[#B5541B]/10 text-[#B5541B]'
                            }`}>
                              {item.guidance ? '' : 'Applies to: '}{item.applies}
                            </span>
                          )}
                        </div>
                        {item.link && (
                          <a 
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#B5541B] hover:text-[#8B3F14] font-medium text-sm transition-colors"
                          >
                            Official Source <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <p className="mt-4 text-[#3B3535]/70 leading-relaxed">
                        {item.description}
                      </p>
                    </div>

                    {/* How ResiAX Helps */}
                    <div className="p-6 lg:p-8 bg-[#FFFAF8]">
                      <h4 className="text-sm font-semibold text-[#B5541B] uppercase tracking-wide mb-4">
                        How ResiAX Helps
                      </h4>
                      <ul className="space-y-3">
                        {item.howResiaxHelps.map((helpItem, helpIndex) => (
                          <li key={helpIndex} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                            <span className="text-[#3B3535]">{helpItem}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}

          {/* Upcoming Feature: National Residents Voice */}
          {complianceData.upcomingFeature && (
            <section className="mb-20">
              <div className="bg-gradient-to-br from-[#B5541B] to-[#8B3F14] rounded-3xl p-8 lg:p-12 text-white">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Network className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{complianceData.upcomingFeature.title}</h3>
                        <span className="text-sm font-semibold text-white/70 uppercase">
                          {complianceData.upcomingFeature.subtitle}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-white/90 text-lg leading-relaxed mb-6">
                      {complianceData.upcomingFeature.description}
                    </p>
                    
                    <ul className="space-y-3">
                      {complianceData.upcomingFeature.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-white/80 shrink-0 mt-0.5" />
                          <span className="text-white/90">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="lg:w-1/3 flex items-center justify-center">
                    <div className="w-48 h-48 rounded-full bg-white/10 flex items-center justify-center">
                      <Users className="w-24 h-24 text-white/30" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* CTA Section */}
          <section className="text-center py-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1F1E1E] mb-4">
              Better evidence and communication can support better compliance.
            </h2>
            <p className="text-[#3B3535]/70 text-lg mb-8 max-w-2xl mx-auto">
              ResiAX supports the communication, evidence and operational workflows that can help
              landlords manage and demonstrate relevant responsibilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleJoinWaitingList}
                className="inline-flex items-center justify-center gap-2 bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl"
              >
                Join the Waiting List
              </button>
              <Link 
                to="/providers"
                data-testid="compliance-providers-link"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#B5541B] text-[#B5541B] hover:bg-[#B5541B] hover:text-white font-semibold px-8 py-4 rounded-full transition-all"
              >
                Explore ResiAX for providers
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Rights grid (relocated from homepage) */}
      <YourRightsSection />
      <Footer />
      <BackToTop />
    </div>
  );
};

export default CompliancePage;
